<script setup lang="ts">
/** Mobile tab bar for the worker PWA. Respects iOS safe-area inset. */
export interface NavItem { value: string; label: string; badge?: number }
defineProps<{ modelValue: string; items: NavItem[] }>()
defineEmits<{ 'update:modelValue': [string] }>()
</script>

<template>
  <nav class="k-bnav" aria-label="Main">
    <button
      v-for="it in items" :key="it.value"
      type="button" class="k-bnav__item"
      :class="{ 'k-bnav__item--active': modelValue === it.value }"
      :aria-current="modelValue === it.value ? 'page' : undefined"
      @click="$emit('update:modelValue', it.value)"
    >
      <span class="k-bnav__icon">
        <slot :name="`icon-${it.value}`" />
        <span v-if="it.badge" class="k-bnav__badge">{{ it.badge > 9 ? '9+' : it.badge }}</span>
      </span>
      <span class="k-bnav__label">{{ it.label }}</span>
    </button>
  </nav>
</template>

<style scoped>
.k-bnav {
  position: fixed; bottom: 0; left: 0; right: 0; z-index: var(--k-z-sticky);
  display: flex; background: var(--k-color-bg);
  border-top: 1px solid var(--k-color-divider);
  padding-bottom: env(safe-area-inset-bottom);
}
.k-bnav__item {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: var(--k-space-1);
  padding: var(--k-space-2) var(--k-space-1) var(--k-space-3);
  color: var(--k-color-text-muted); cursor: pointer;
  transition: color var(--k-duration-base) var(--k-easing-standard);
}
.k-bnav__item--active { color: var(--k-color-primary); }
.k-bnav__icon { position: relative; display: inline-flex; }
.k-bnav__badge {
  position: absolute; top: -4px; right: -8px;
  min-width: 16px; height: 16px; padding: 0 4px;
  border-radius: var(--k-radius-pill);
  background: var(--k-color-primary); color: var(--k-color-text-inverse);
  font-size: 10px; line-height: 16px; text-align: center;
  font-weight: var(--k-font-weight-medium);
}
.k-bnav__label { font-size: 10px; line-height: 14px; font-weight: var(--k-font-weight-medium); }
</style>
